import type { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { Field, FieldError, FieldLabel } from '@talentmesh/ui/components/field';
import { RHFDefaultInput } from '@talentmesh/ui/components/rhf-default-input';
import { RHFNumericInput } from '@talentmesh/ui/components/rhf-numeric-input';
import { type InputProps } from '@talentmesh/ui/components/input';
import { cn } from '@talentmesh/ui/lib/utils';

export type RHFInputProps = Omit<InputProps, 'name' | 'value' | 'defaultValue' | 'onChange' | 'onBlur' | 'ref'> & {
    name: string;
    label?: ReactNode;
    hideLabel?: boolean;
    fullWidth?: boolean;
    displayValue?: string;
    inputOverlay?: ReactNode;
    fieldClassName?: string;
    fieldDataCy?: string;
    useNumberFormatter?: boolean;
    hideFieldError?: boolean;
};

function RHFInput({
    name,
    label,
    hideLabel,
    id,
    fullWidth,
    displayValue,
    inputOverlay,
    className,
    fieldClassName,
    type = 'text',
    fieldDataCy,
    useNumberFormatter,
    disabled,
    autoComplete = 'off',
    hideFieldError,
    ...inputRest
}: RHFInputProps) {
    const { formState } = useFormContext();
    const error = formState.errors[name];
    const inputId = id ?? name;

    return (
        <Field
            className={cn(fullWidth && 'w-full', fieldClassName)}
            data-invalid={error ? true : undefined}
            data-cy={fieldDataCy}
        >
            {label && !hideLabel ? <FieldLabel htmlFor={inputId}>{label}</FieldLabel> : null}
            <div className="relative">
                {useNumberFormatter ? (
                    <RHFNumericInput
                        name={name}
                        id={inputId}
                        className={className}
                        disabled={disabled}
                        autoComplete={autoComplete}
                        {...inputRest}
                    />
                ) : (
                    <RHFDefaultInput
                        name={name}
                        id={inputId}
                        type={type}
                        className={className}
                        displayValue={displayValue}
                        disabled={disabled}
                        autoComplete={autoComplete}
                        {...inputRest}
                    />
                )}
                {inputOverlay}
            </div>
            {!hideFieldError && <FieldError>{error?.message ? String(error.message) : null}</FieldError>}
        </Field>
    );
}

export { RHFInput };
